import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import "./AccessManagerPanel.css";

/**
 * DeleteDepartmentDialog — Radix confirmation dialog for department deletion.
 *
 * Names the department, lists the scopes lost, states the member impact from
 * `member_count`, and explains the soft-delete semantics (deactivated, not
 * erased, can be restored by re-activating). No typed-confirmation gate
 * (unlike RevokeDialog) since the action is reversible. Dismissing without
 * confirming is a no-op.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is visible.
 * @param {{ id: number, name: string, scopes: string[], member_count: number }|null} props.department
 * @param {() => void} props.onClose - Called when the dialog should close.
 * @param {() => Promise<void>} props.onConfirm - Called on confirmation.
 */
export default function DeleteDepartmentDialog({ open, department, onClose, onConfirm }) {
  if (!department) return null;

  const { name, scopes = [], member_count: memberCount = 0 } = department;

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="access-manager-dialog__overlay" />
        <Dialog.Content className="access-manager-dialog__content">
          <Dialog.Title className="access-manager-dialog__title">
            Delete Department
          </Dialog.Title>
          <Dialog.Description className="access-manager-dialog__description">
            You are about to delete <strong>{name}</strong>.
          </Dialog.Description>

          <div className="access-manager-dialog__warning" role="note">
            Deleting <strong>{name}</strong> removes the{" "}
            {scopes.length > 0 ? (
              <>
                {scopes.map((s, i) => (
                  <span key={s}>
                    <code>{s}</code>
                    {i < scopes.length - 1 ? ", " : ""}
                  </span>
                ))}
              </>
            ) : (
              "assigned"
            )}{" "}
            {scopes.length === 1 ? "scope" : "scopes"} from its{" "}
            {memberCount} {memberCount === 1 ? "member" : "members"}.
          </div>

          <p className="delete-department-dialog__soft-delete">
            This is a soft delete — the department is deactivated, not erased, and can
            be restored by re-activating it.
          </p>

          <div className="access-manager-dialog__actions">
            <Dialog.Close asChild>
              <button
                type="button"
                className="access-manager-dialog__btn access-manager-dialog__btn--cancel"
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              className="access-manager-dialog__btn access-manager-dialog__btn--confirm"
              onClick={onConfirm}
            >
              Delete
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
